import React from 'react';
import PreviewCanvas from './PreviewCanvas';
import { MATERIALS, FINISHES } from './types';

interface ReviewStepProps {
  image: string | null;
  croppedImageUrl: string | null;
  selectedSize: string | null;
  selectedSizeId: number | null;
  selectedMaterial: string;
  selectedFinish: string;
  borderColor: string;
  sizes: Array<{ id: number; price: number }>;
  getCurrentPrice: () => number;
  previewCanvasUrl: string | null;
  rightsConfirmed: boolean;
  onMaterialChange: (material: string) => void;
  onFinishChange: (finish: string) => void;
  onBorderColorChange: (color: string) => void;
  onPreviewReady: (dataUrl: string) => void;
  onRightsConfirmedChange: (confirmed: boolean) => void;
}

const ReviewStep: React.FC<ReviewStepProps> = ({
  image,
  croppedImageUrl,
  selectedSize,
  selectedSizeId,
  selectedMaterial,
  selectedFinish,
  borderColor,
  sizes,
  getCurrentPrice,
  previewCanvasUrl,
  rightsConfirmed,
  onMaterialChange,
  onFinishChange,
  onBorderColorChange,
  onPreviewReady,
  onRightsConfirmedChange
}) => {
  const finishObj = FINISHES.find(f => f.value === selectedFinish);
  const frameSurcharge = finishObj && finishObj.frameSurcharge > 0 && selectedSize === '10x20' ? finishObj.frameSurcharge : 0;
  const hangerSurcharge = finishObj ? finishObj.hangerSurcharge : 0;
  const basePrice = selectedSizeId ? (sizes.find(s => s.id === selectedSizeId)?.price || 0) : 0;
  const hasFrame = finishObj ? finishObj.value.includes('floater') : false;
  const showCanvasFrame = selectedMaterial === 'Canvas' && !hasFrame;
  const borderColors = [
    { label: 'White', value: '#ffffff' },
    { label: 'Black', value: '#111111' },
    { label: 'Natural', value: '#e8ddcf' },
    { label: 'Walnut', value: '#6b4f3a' },
    { label: 'Charcoal', value: '#3a3a3a' },
    { label: 'Cream', value: '#f6f1e8' }
  ];

  return (
    <div className="wizard-step">
      <h2>Customize & Review</h2>
      <p className="step-description">Review your selections and customize your print</p>
      <div className="review-section">
        <div className="review-image">
          <PreviewCanvas
            croppedImageUrl={croppedImageUrl}
            selectedFinish={selectedFinish}
            selectedMaterial={selectedMaterial}
            borderColor={borderColor}
            onPreviewReady={onPreviewReady}
          />
          {previewCanvasUrl ? (
            <div
              className={`preview-frame${showCanvasFrame ? ' canvas-frame' : ''}`}
            >
              <img 
                src={previewCanvasUrl} 
                alt="Your finished print preview" 
                className="preview-image"
              />
            </div>
          ) : croppedImageUrl ? (
            <div
              className={`preview-frame${showCanvasFrame ? ' canvas-frame' : ''}`}
            >
              <img 
                src={croppedImageUrl} 
                alt="Your cropped print" 
                className="preview-image"
              />
            </div>
          ) : image ? (
            <div
              className={`preview-frame${showCanvasFrame ? ' canvas-frame' : ''}`}
            >
              <img 
                src={image} 
                alt="Your print" 
                className="preview-image"
              />
            </div>
          ) : (
            <div className="preview-placeholder">
              <i className="fas fa-image"></i>
              <p>No image available</p>
            </div>
          )}
        </div>
        <div className="review-options">
          <div className="review-item">
            <label>Size:</label>
            <span>{selectedSize}</span>
          </div>
          <div className="form-group">
            <label htmlFor="review-material">Material</label>
            <select
              id="review-material"
              value={selectedMaterial}
              onChange={(e) => onMaterialChange(e.target.value)}
            >
              {MATERIALS.map((material) => (
                <option key={material} value={material}>{material}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="review-finish">Frame & Hanging</label>
            <select
              id="review-finish"
              value={selectedFinish}
              onChange={(e) => onFinishChange(e.target.value)}
            >
              {FINISHES.map((finish) => (
                <option key={finish.value} value={finish.value}>{finish.label}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="review-wrap-color">Canvas Wrap Color</label>
            <select
              id="review-wrap-color"
              value={borderColor}
              onChange={(e) => onBorderColorChange(e.target.value)}
            >
              {borderColors.map((color) => (
                <option key={color.value} value={color.value}>{color.label}</option>
              ))}
            </select>
          </div>
          <div className="price-summary">
            <div className="price-row">
              <span>Base Price:</span>
              <span>${basePrice.toFixed(2)}</span>
            </div>
            {frameSurcharge > 0 && (
              <div className="price-row">
                <span>Floater Frame (10x20):</span>
                <span>+${frameSurcharge.toFixed(2)}</span>
              </div>
            )}
            {hangerSurcharge > 0 && (
              <div className="price-row">
                <span>Wire Hanging:</span>
                <span>+${hangerSurcharge.toFixed(2)}</span>
              </div>
            )}
            <div className="price-row">
              <span>Shipping:</span>
              <span>Calculated at checkout</span>
            </div>
            <div className="price-row total">
              <span>Total:</span>
              <span>${getCurrentPrice().toFixed(2)}</span>
            </div>
          </div>
          <label
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '10px',
              marginTop: '16px',
              cursor: 'pointer',
              fontSize: '0.9em',
              lineHeight: 1.5
            }}
          >
            <input
              type="checkbox"
              checked={rightsConfirmed}
              onChange={(e) => onRightsConfirmedChange(e.target.checked)}
              style={{ marginTop: '3px', flexShrink: 0 }}
            />
            <span>
              I confirm that I own this image or have permission from the
              rights holder to have it printed, per the{' '}
              <a href="/copyright" target="_blank" rel="noreferrer">Copyright &amp; IP Policy</a>.
            </span>
          </label>
        </div>
      </div>
    </div>
  );
};

export default ReviewStep;
